import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useImportCustomers, usePostAllCustomers, useAllCustomersExport } from '../hooks/api/customerListApi';
import Add from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import DataTableRowHoverButton from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableRowHoverButton';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import FullList from '../../../components/FullList';
import { useHistory } from 'react-router';
import { customersColumns } from '../enums/columns';
import { useUsersEnum } from '../../../hooks/api/usersEnumApi';
import { FormattedMessage, useIntl } from 'react-intl';
import Tooltip from '@material-ui/core/Tooltip';
import FileUpload from '../../../components/FileUpload/FileUpload';
import FileExport from '../../../components/FileExport/FileExport';
import useAppContext from '../../../hooks/context/useAppContext';
import userRoles from '../../../constants/userRoles';
import { handleUploadResult } from '../../../utils/fileUploadErrorsCheck';

const customerDashboard = 'customerDashboard';
const customerEdit = 'customers';
const customerNew = 'customers/new';
const sessionFilterStorageName = 'customersFilter';

const columns = [
  customersColumns.state,
  customersColumns.sapId,
  customersColumns.organizationName,
  customersColumns.organizationNumber,
  customersColumns.kam,
  customersColumns.representatives,
  customersColumns.controls,
];

const CustomerList = () => {
  const { getAllCustomers, data, loading, error } = usePostAllCustomers();
  const { getAllCustomersExport, data: exportData, error: exportError, loading: exportLoading } = useAllCustomersExport();
  const { uploadCustomers, data: importData, loading: importLoading, error: importError } = useImportCustomers();
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refresh, setRefresh] = useState(null);
  const [searchQuery, setSearchQuery] = useState({});

  const history = useHistory();
  const { getUsersEnum } = useUsersEnum();
  const { user } = useAppContext();
  const intl = useIntl();

  useEffect(() => {
    handleUploadResult(importData, importError, setSuccessMessage, setErrorMessage, setRefresh);
  }, [importData, importError]);

  const storeFilter = JSON.parse(sessionStorage.getItem(sessionFilterStorageName));

  const initialValues = {
    filters: storeFilter?.columnsFilter || {
      organizationId: user?.customerIds,
      state: [0],
    },
    page: storeFilter?.page || 0,
    rowsPerPage: storeFilter?.size || user?.initialRowsPerPage,
    order: storeFilter?.order || 'asc',
    orderBy: storeFilter?.orderBy || 'organizationName',
    fulltext: storeFilter?.fulltext || '',
    filterOptions: storeFilter?.filterOptions || {
      state: [{ id: 0 }, { id: 1 }],
    },
  };

  const toolbarComponents = [
    <FileUpload upload={uploadCustomers} tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.import'} />} key={0} />,
    <FileExport
      getAllEntityExport={getAllCustomersExport}
      tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.export'} />}
      fileName={'EEM_zakaznici_export.xlsx'}
      data={exportData}
      searchQuery={searchQuery}
      exportError={exportError}
      setErrorMessage={setErrorMessage}
      key={'toolbar-file-export'}
    />,
    <Tooltip title={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.add'} />} arrow key={1}>
      <IconButton size={'small'} onClick={() => history.push(`${customerNew}`)}>
        <Add fontSize={'large'} />
      </IconButton>
    </Tooltip>,
  ];

  const columnFilters = useMemo(
    () => ({
      [customersColumns.kam.id]: async () => getUsersEnum('KAM'),
      [customersColumns.state.id]: () => [
        {
          id: 1,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.active' }),
        },
        {
          id: 0,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.inactive' }),
        },
      ],
    }),
    [getUsersEnum, intl],
  );

  const onFilterOpen = useCallback(
    async (column) => {
      return columnFilters?.[column] && columnFilters[column]();
    },
    [columnFilters],
  );

  const processData = (dataToProcess) => {
    return {
      data: dataToProcess.data.reduce((output, line) => {
        const currentLine = { ...line };
        currentLine.organizationName = line.name;
        currentLine.kam = line.users
          .filter((user) => user.role === 'KAM')
          .map((user) => `${user.firstname} ${user.surname}`)
          .join(', ');
        currentLine.representatives = line.users
          .filter((user) => user.role === 'CUSTOMER' || user.role === 'CUSTOMER_VIEWER')
          .map((user) => `${user.firstname} ${user.surname}`)
          .join(', ');
        currentLine.idSap = line?.idExternal;
        currentLine.controls = (
          <>
            <DataTableRowHoverButton>
              {user.role === userRoles.ADMIN ? (
                <IconButton
                  size={'small'}
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`${customerEdit}/${line.id}`);
                  }}>
                  <SupervisorAccountIcon fontSize={'large'} />
                </IconButton>
              ) : null}
              <IconButton size={'small'}>
                <ArrowForwardIosIcon fontSize={'large'} />
              </IconButton>
            </DataTableRowHoverButton>
          </>
        );
        currentLine.state = currentLine.state ? (
          <FormattedMessage id={'app.pages.customerListPage.state.active'} />
        ) : (
          <FormattedMessage id={'app.pages.customerListPage.state.inactive'} />
        );
        output.push(currentLine);
        return output;
      }, []),
      totalCount: dataToProcess.totalCount,
    };
  };

  return (
    <>
      <FullList
        getData={getAllCustomers}
        loading={loading || importLoading || exportLoading}
        error={error}
        data={data}
        processData={processData}
        columns={columns}
        initialValues={initialValues}
        showToolbarIcons={user.role === userRoles.ADMIN}
        toolbarComponents={toolbarComponents}
        onLineClick={(line) => {
          history.push(`${customerDashboard}/${line.id}`);
        }}
        onFilterOpen={onFilterOpen}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        refresh={refresh}
        setRefresh={setRefresh}
        setSearchQuery={setSearchQuery}
        sessionFilterStorageName={sessionFilterStorageName}
      />
    </>
  );
};

CustomerList.propTypes = {};

export default CustomerList;
